import Service, { type Registry as Services, service } from '@ember/service';
import type { AjaxError } from 'ember-ajax/errors';
import { isBadRequestError } from 'ember-ajax/errors';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import ENV from 'lumen/config/environment';
import type AjaxService from 'lumen/services/ajax';

export type LookerUserType = 'custom_reporting' | 'daas';

export interface LookerEmbedAcquireParams {
  dashboardId?: string;
  marketId?: string;
  userType: LookerUserType;
}

interface LookerSessionTokens {
  api_token: string;
  api_token_ttl: number;
  navigation_token: string;
  navigation_token_ttl: number;
  session_reference_token_ttl: number;
}

interface AcquireResponse extends LookerSessionTokens {
  authentication_token: string;
  authentication_token_ttl: number;
}

/**
 * Owns the Looker cookieless embed session: acquires it via the BE, builds the
 * single-use /login/embed/ URL and answers the iframe's `session:tokens:request`
 * messages (see GeneralEmbeddedApp) via the generate-tokens endpoint.
 *
 * The session_reference_token never reaches the FE — the BE keeps it in the
 * Django session and reuses it across acquires.
 */
export default class LookerEmbedSessionService extends Service {
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];

  #acquireParams?: LookerEmbedAcquireParams;
  #authenticationToken?: string;
  #firstRequestAnswered = false;
  #generateTokensRequest?: Promise<LookerSessionTokens | undefined>;
  #tokens?: LookerSessionTokens;
  /**
   * Set by the active embedding route; invoked when the Looker embed session
   * expires (generate-tokens returns session_reference_token_ttl 0 or a 400).
   * The owner must re-run acquire and rebuild the iframe.
   */
  onSessionExpired?: () => void;

  get #embedDomain(): string {
    return ENV.isDevelopmentOrTest ? 'http://localhost:4200' : this.ajax.host;
  }

  async #generateTokens(): Promise<LookerSessionTokens | undefined> {
    try {
      const response = await this.ajax.put<LookerSessionTokens>(
        this.#sessionUrl('generate-tokens'),
        {
          contentType: 'application/json',
          data: {
            api_token: this.#tokens!.api_token,
            navigation_token: this.#tokens!.navigation_token,
          },
        },
      );

      if (response.session_reference_token_ttl === 0) {
        this.onSessionExpired?.();
        return undefined;
      }

      this.#tokens = response;
      return response;
    } catch (error) {
      if (isBadRequestError(error as AjaxError)) {
        // no acquired session in the BE session store — re-acquire
        this.onSessionExpired?.();
      } else {
        this.errorTracking.captureException(error);
      }
      return undefined;
    }
  }

  #postTokens(iframe: HTMLIFrameElement, tokens: LookerSessionTokens): void {
    iframe.contentWindow?.postMessage(
      JSON.stringify({ type: 'session:tokens', ...tokens }),
      ENV.lookerEmbedURL,
    );
  }

  #sessionUrl(endpoint: 'acquire' | 'generate-tokens'): string {
    const { marketId, userType } = this.#acquireParams!;

    return formatUrl(
      `/api/v3/custom-reporting/embed/session/${endpoint}/`,
      { user_type: userType, ...(marketId ? { market_id: marketId } : {}) },
      this.ajax.host,
    );
  }

  async acquire(params: LookerEmbedAcquireParams): Promise<void> {
    this.reset();
    this.#acquireParams = params;

    const response = await this.ajax.post<AcquireResponse>(
      this.#sessionUrl('acquire'),
      {
        contentType: 'application/json',
        data: {
          embed_domain: this.#embedDomain,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(params.dashboardId ? { dashboard_id: params.dashboardId } : {}),
        },
      },
    );

    this.#authenticationToken = response.authentication_token;
    this.#tokens = {
      api_token: response.api_token,
      api_token_ttl: response.api_token_ttl,
      navigation_token: response.navigation_token,
      navigation_token_ttl: response.navigation_token_ttl,
      session_reference_token_ttl: response.session_reference_token_ttl,
    };
  }

  buildLoginUrl(targetPath: string): string {
    if (!this.#authenticationToken || !this.#tokens) {
      throw new Error(
        'looker-embed-session: acquire() must be called (again) before buildLoginUrl() — the authentication token is single-use',
      );
    }

    // embed_navigation_token and embed_domain must be nested inside the
    // pre-encoded target path rather than appended as outer query params —
    // confirmed against this Looker instance; the outer-query-param form
    // (matching the public docs/SDK source) causes a server-side crash on login.
    const innerUrl = new URL(`${ENV.lookerEmbedURL}${targetPath}`);
    innerUrl.searchParams.set(
      'embed_navigation_token',
      this.#tokens.navigation_token,
    );
    innerUrl.searchParams.set('embed_domain', this.#embedDomain);
    const srcToEncode = `${innerUrl.pathname}${innerUrl.search}`;

    const url = new URL(
      `${ENV.lookerEmbedURL}/login/embed/${encodeURIComponent(srcToEncode)}`,
    );
    url.searchParams.set(
      'embed_authentication_token',
      this.#authenticationToken,
    );

    // single-use, 30s TTL
    this.#authenticationToken = undefined;

    return url.toString();
  }

  reset(): void {
    this.#acquireParams = undefined;
    this.#authenticationToken = undefined;
    this.#firstRequestAnswered = false;
    this.#generateTokensRequest = undefined;
    this.#tokens = undefined;
  }

  /**
   * Looker asks for tokens repeatedly (incl. mid-navigation) and every request
   * must be answered — concurrent requests share one in-flight BE call.
   */
  async respondToTokensRequest(iframe: HTMLIFrameElement): Promise<void> {
    if (!this.#tokens || !this.#acquireParams) {
      // no acquired session (e.g. message from a stale iframe) — nothing to say
      return;
    }

    if (!this.#firstRequestAnswered) {
      this.#firstRequestAnswered = true;
      this.#postTokens(iframe, this.#tokens);
      return;
    }

    if (!this.#generateTokensRequest) {
      this.#generateTokensRequest = (async (): Promise<
        LookerSessionTokens | undefined
      > => {
        const tokens = await this.#generateTokens();
        this.#generateTokensRequest = undefined;
        return tokens;
      })();
    }

    const tokens = await this.#generateTokensRequest;
    if (tokens) {
      this.#postTokens(iframe, tokens);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'looker-embed-session': LookerEmbedSessionService;
  }
}
